import axiosInstance from "../../api/axios";

/** Matches backend validation in ExchangeRateService */
export const SUPPORTED_CURRENCIES = ['EUR', 'USD', 'GBP', 'TND'];

const formatDateKey = (value) => {
    if (!value) return '';
    return String(value).slice(0, 10);
};

/**
 * Backend stores one row per currency (currency_code + rate).
 * UI groups rows that share the same start/end period.
 */
export const groupExchangeRatesByPeriod = (items = []) => {
    const groups = new Map();

    items.forEach((item) => {
        const startDate = formatDateKey(item.start_date);
        const endDate = formatDateKey(item.end_date);
        const key = `${startDate}|${endDate}`;

        if (!groups.has(key)) {
            groups.set(key, {
                groupKey: key,
                start_date: startDate,
                end_date: endDate,
                rates: {},
                entries: [],
            });
        }

        const group = groups.get(key);
        const currencyCode = item.currency_code;

        if (currencyCode && item.rate !== null && item.rate !== undefined && item.rate !== '') {
            const rateValue = String(item.rate);
            group.rates[currencyCode] = rateValue;
            group.entries.push({
                id: item.id,
                currency_code: currencyCode,
                rate: rateValue,
            });
        }
    });

    return Array.from(groups.values());
};

const extractErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    if (!data) return fallback;

    if (typeof data.error === 'string') return data.error;
    if (typeof data.message === 'string') return data.message;
    if (data.errors) {
        const first = Object.values(data.errors).flat()[0];
        if (first) return first;
    }

    return fallback;
};

const buildRatePayload = (startDate, endDate, currency, rate) => ({
    start_date: startDate,
    end_date: endDate,
    currency_code: currency,
    rate: String(rate),
});

const ExchangeService = {
    getAllExchangeRates: async () => {
        try {
            const response = await axiosInstance.get("/manual_convert");
            const raw = response.data.exchangeRates || [];
            return { success: true, data: groupExchangeRatesByPeriod(raw), raw };
        } catch (error) {
            console.error("Failed to fetch exchange rates", error);
            throw error;
        }
    },

    getExchangeRatesByDateRange: async (startDate, endDate) => {
        try {
            const response = await axiosInstance.get(
                `/manual_convert?start_date=${startDate}&end_date=${endDate}`
            );
            const raw = response.data.exchangeRates || [];
            return { success: true, data: groupExchangeRatesByPeriod(raw), raw };
        } catch (error) {
            console.error("Failed to fetch exchange rates by date range", error);
            throw error;
        }
    },

    createExchangeRatePeriod: async ({ start_date, end_date, rates }) => {
        try {
            const created = [];
            for (const { currency, value } of rates) {
                const response = await axiosInstance.post(
                    "/manual_convert",
                    buildRatePayload(start_date, end_date, currency, value)
                );
                created.push(response.data);
            }
            return { success: true, data: created };
        } catch (error) {
            console.error("Failed to create exchange rate period", error);
            throw error;
        }
    },

    updateExchangeRatePeriod: async ({ start_date, end_date, previousEntries = [], rates }) => {
        try {
            const previousByCurrency = Object.fromEntries(
                previousEntries.map((entry) => [entry.currency_code, entry])
            );
            const nextCurrencies = new Set(rates.map((row) => row.currency));

            for (const entry of previousEntries) {
                if (!nextCurrencies.has(entry.currency_code)) {
                    await axiosInstance.delete(`/manual_convert/${entry.id}`);
                }
            }

            for (const row of rates) {
                const payload = buildRatePayload(start_date, end_date, row.currency, row.value);
                const existing = previousByCurrency[row.currency];

                if (existing) {
                    await axiosInstance.post(`/manual_convert/${existing.id}`, payload);
                } else {
                    await axiosInstance.post("/manual_convert", payload);
                }
            }

            return { success: true };
        } catch (error) {
            console.error("Failed to update exchange rate period", error);
            throw error;
        }
    },

    deleteExchangeRateGroup: async (entries = []) => {
        try {
            for (const entry of entries) {
                await axiosInstance.delete(`/manual_convert/${entry.id}`);
            }
            return { success: true };
        } catch (error) {
            console.error("Failed to delete exchange rate group", error);
            throw error;
        }
    },

    extractErrorMessage,
};

export default ExchangeService;
